import { useEffect, useRef, useState } from 'react';
import { api } from '@/api/client';
import { PickerGlyph } from './pickerIcons';

interface Extension {
  id: string;
  name: string;
  domain?: string;
  icon?: string;
  summary?: string;
  installed?: boolean;
  installedNote?: string | null;
  install?: {
    kind: 'url' | 'command' | 'package' | 'instructions';
    url?: string;
    command?: string;
    args?: string[];
    notes?: string;
  };
}
interface ExtensionsResponse { extensions: Extension[] }

/**
 * Extensions sidebar panel — port of legacy data-panel="extensions".
 *
 * Lists Brew add-ons (Brew Code for VS Code, Brew CLI, Pandoc,
 * python-pptx, etc.) grouped by domain (Editor, Terminal, Documents,
 * Presentations, Vision). Each card surfaces install method.
 */
export function ExtensionsPanel({ hovered }: { hovered: boolean }) {
  const [rows, setRows] = useState<Extension[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  function load(): void {
    setError(null);
    api.get<ExtensionsResponse>('/api/extensions')
      .then((r) => setRows(r.extensions))
      .catch((e) => setError((e as Error).message));
  }
  // Fetch LAZILY — only the first time the user actually opens the flyout. The
  // panel renders nothing unless `hovered` (below), and /api/extensions runs
  // real tool-detection probes on the server, so fetching on mount made every
  // page boot pay for a panel the user rarely opens. Once, on first open.
  const didFetch = useRef(false);
  useEffect(() => {
    if (hovered && !didFetch.current) {
      didFetch.current = true;
      load();
    }
  }, [hovered]);

  function openInstall(ext: Extension): void {
    const install = ext.install;
    if (!install) return;
    if (install.kind === 'url' && install.url) {
      window.open(install.url, '_blank', 'noopener,noreferrer');
    } else if (install.kind === 'command' && install.command) {
      // Command-mode (e.g. `code --install-extension datacaffe.brew-code`).
      // Show the exact command so the user can run it; legacy did the same.
      const cmd = `${install.command} ${(install.args ?? []).join(' ')}`.trim();
      alert(`Run this in your terminal:\n\n${cmd}`);
    } else if (install.kind === 'package' && install.command) {
      const cmd = `${install.command} ${(install.args ?? []).join(' ')}`.trim();
      alert(`Run this in your terminal:\n\n${cmd}`);
    } else if (install.notes) {
      alert(install.notes);
    }
  }

  // Available (installable) extensions go in the hover flyout. Installed /
  // backend ones (e.g. Ollama) are not user-selected actives, so nothing pins.
  const availableRows = (rows ?? []).filter((e) => !e.installed && e.install);

  return (
    <>
      {hovered && (
        <div className="snav__pop" role="menu">
          <div className="snav__pop-title">Add an extension</div>
          {!rows && !error && <div className="snav__pop-msg">Loading…</div>}
          {error && <div className="snav__pop-msg snav__pop-msg--err">{error}</div>}
          {rows && availableRows.length === 0 && <div className="snav__pop-msg">Nothing to install.</div>}
          {availableRows.map((e) => (
            <button
              key={e.id}
              type="button"
              className="snav__pop-item"
              onClick={() => openInstall(e)}
              data-tip={`Install ${e.name}`}
            >
              <PickerGlyph icon={e.icon ?? null} label={e.name} size={26} />
              <span className="snav__pop-item__name">{e.name}</span>
              <span className="snav__pop-item__tag">{e.install?.kind === 'url' ? 'install' : 'page ↗'}</span>
            </button>
          ))}
        </div>
      )}
    </>
  );
}
